/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
// tslint:disable:no-any no-string-literal max-func-body-length cyclomatic-complexity max-line-length no-floating-promises no-return-await
import MsDyn365 from '@msdyn365-commerce/core';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IDictionary } from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult } from '@msdyn365-commerce/global-state';
import { Cart, CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { reaction } from 'mobx';
import * as React from 'react';
import getNotificationMessage, { createNotificationMessage } from '../../data-actions/product-info/get-notification-message';
import { NotificationType } from '../../models/notification-message-type';
import { ICittaCreateGiftCardData } from './citta-create-gift-card.data';
import { ICittaCreateGiftCardProps } from './citta-create-gift-card.props.autogenerated';

export interface ICittaCreateGiftCardViewProps extends ICittaCreateGiftCardProps<ICittaCreateGiftCardData> {
    GiftCardCreationModuleProps: IModuleProps;
    GiftCardWrapNodeProps: INodeProps;
    GiftCardRight: React.ReactNode;
    GiftCardErrorNodeProps: INodeProps;
    GiftCardErrors: React.ReactNode;
    GiftCardAddToCartErrorsNodeProps: INodeProps;
    GiftCardAddToCartErrors: React.ReactNode;
}

interface ICittaCreateGiftCardState {
    isEmail: boolean;
    isEmailOptSelectedClass: string;
    isPrintOptSelectedClass: string;
    cardAmount?: string;
    isAmount1SelectedClass?: string;
    isAmount2SelectedClass?: string;
    isAmount3SelectedClass?: string;
    isThumb1SelectedClass?: string;
    isThumb2SelectedClass?: string;
    isThumb3SelectedClass?: string;
    isThumb4SelectedClass?: string;
    selectedThumb?: string;
    selectedThumbId?: number;
    thumbColor?: string;
    isPaletteColor1SelectedClass?: string;
    isPaletteColor2SelectedClass?: string;
    isPaletteColor3SelectedClass?: string;
    selectedPalette?: string;
    selectedPaletteId?: number;
    message?: string;
    charactersLeft?: number;
    sendDate?: string;
    defaultDate?: string;
    isSendDateChangedClass?: string;
    sendDateErrorClass: string;
    amountErrorClass: string;
    recipientEmailErrorClass: string;
    recipientNameErrorClass: string;
    fromNameErrorClass: string;
    messageErrorClass: string;
    apiErrorClass: string;
    userInput: IDictionary<string>;
    inputLabelClasses: IDictionary<string>;
    disabledOverride: boolean;
    showWishlistText: boolean;
    showRemoveFromWishlistButton: boolean;
    showAlert: boolean;
    wishlistMessage: string;
    disableWishlistButton: boolean;
    addToCartFailed: boolean;
    addToCartErrorMessage: string;
    isWishlistWaiting: boolean;
}

enum Market {
    NZ = 'NZL',
    AU = 'AUS',
}

/**
 *
 * CittaGiftCardCreation component
 * @extends {React.PureComponent<ICittaCreateGiftCardProps<ICittaCreateGiftCardData>>}
 */
class CittaGiftCardCreation extends React.PureComponent<ICittaCreateGiftCardProps<ICittaCreateGiftCardData>, ICittaCreateGiftCardState> {
    private recipientEmailRef: React.RefObject<HTMLInputElement>;
    private sendDateRef: React.RefObject<HTMLInputElement>;
    private amountRef: React.RefObject<HTMLInputElement>;
    private recipientNameRef: React.RefObject<HTMLInputElement>;
    private fromNameRef: React.RefObject<HTMLInputElement>;
    private messageRef: React.RefObject<HTMLTextAreaElement>;
    private product: SimpleProduct | null = null;
    private userChoice: IDictionary<string> = {};

    private hasGiftCardLineItem: number = 0;

    constructor(props: ICittaCreateGiftCardProps<ICittaCreateGiftCardData>) {
        super(props);
        const { config, resources } = this.props;

        this.state = {
            isEmail: true,
            isEmailOptSelectedClass: 'selected',
            isPrintOptSelectedClass: '',
            isThumb1SelectedClass: 'selected',
            isThumb2SelectedClass: '',
            isThumb3SelectedClass: '',
            isThumb4SelectedClass: '',
            selectedThumb: 'thumb1',
            selectedThumbId: 0,
            thumbColor: config.thumb1Color,
            cardAmount: config.opt1,
            isPaletteColor1SelectedClass: 'selected',
            isPaletteColor2SelectedClass: '',
            isPaletteColor3SelectedClass: '',
            selectedPalette: 'palette1',
            selectedPaletteId: 0,
            message: '',
            charactersLeft: config.messageCharactersLimit,
            sendDate: '',
            defaultDate: '',
            isSendDateChangedClass: '',
            sendDateErrorClass: 'hide',
            amountErrorClass: 'hide',
            recipientEmailErrorClass: 'hide',
            recipientNameErrorClass: 'hide',
            fromNameErrorClass: 'hide',
            messageErrorClass: 'hide',
            apiErrorClass: 'hide',
            userInput: {
                cardtype: 'email',
                recipientemail: '',
                senddate: '',
                amount: config.opt1 || '',
                cardcolour: config.thumb1Color || '',
                colourpalette: config.thumb1Palette1Color || '',
                recipientname: resources.recipientLabel,
                fromname: '',
                message: ''
            },
            inputLabelClasses: {
                recipientemail: '',
                amount: '',
                recipientname: '',
                fromname: '',
                message: ''
            },
            disabledOverride: false,
            showWishlistText: false,
            showRemoveFromWishlistButton: false,
            disableWishlistButton: this.props.context.request.user.isAuthenticated, // If user is authenticated, disable button until we know item status
            showAlert: true,
            wishlistMessage: this.props.resources.addToWishlistMessage || 'Added to your wishlist',
            addToCartFailed: false,
            addToCartErrorMessage: '',
            isWishlistWaiting: false
        };

        this.recipientEmailRef = React.createRef();
        this.amountRef = React.createRef();
        this.recipientNameRef = React.createRef();
        this.fromNameRef = React.createRef();
        this.messageRef = React.createRef();
        this.sendDateRef = React.createRef();
    }

    public async componentDidMount(): Promise<void> {
        this._getProduct().then(result => { this.product = result; });
        // @ts-ignore: Compiler not reconizing condition check for function params
        reaction(
            () => this.props.data.cart && this.props.data.cart.result,
            () => {
                this._loadData();
            }
        );
    }

    public render(): JSX.Element | null {

        const CittaGiftCardBalanceCreationViewProps = {
            ...this.props,
            GiftCardCreationModuleProps: {
                moduleProps: this.props,
                className: 'citta-create-gift-card'
            },
            GiftCardWrapNodeProps: {
                className: 'giftCardWrap'
            },
            GiftCardErrorNodeProps: {
                className: 'errors'
            },
            GiftCardRight: this._renderGiftCardRight(),
            GiftCardErrors: this._renderErrors(),
            GiftCardAddToCartErrorsNodeProps: {
                className: 'product-add-to-cart'
            },
            GiftCardAddToCartErrors: this._renderAddToCartErrorBlock()
        };

        return this.props.renderView(CittaGiftCardBalanceCreationViewProps) as React.ReactElement;
    }

    private _renderGiftCardRight = (): JSX.Element => {
        const { resources } = this.props;
        return (
            <div className='giftCardRight'>
                <div className='giftCardSteps'>
                    <div className='giftCardStep'>
                        <div className='rowDetails'>
                            <div className={`giftCardOpt amountinput ${this.state.inputLabelClasses[`amount`].toString()}`}>
                                {/* <label className='label'>{opt4Label}</label> */}
                                <input ref={this.amountRef} type='text' onChange={this._onOptsChangeEvent('opt_4')} className='giftCardOpt-input' aria-label={resources.opt4Placeholder} placeholder={resources.opt4Placeholder} />
                            </div>
                            <div className={`datepicker ${this.state.isSendDateChangedClass}`}>
                                {this.state.defaultDate &&
                                    <input
                                        ref={this.sendDateRef}
                                        min={this._getMinDate()}
                                        type='date'
                                        role='button'
                                        className='datepicker-input'
                                        aria-label={resources.datePlaceholder}
                                        placeholder={resources.datePlaceholder}
                                        value={this.state.defaultDate}
                                        onChange={this._capture('senddate', this.sendDateRef)}
                                    />
                                }
                                {!this.state.defaultDate &&
                                    <input
                                        ref={this.sendDateRef}
                                        min={this._getMinDate()}
                                        type='date'
                                        role='button'
                                        className='datepicker-input'
                                        aria-label={resources.datePlaceholder}
                                        placeholder={resources.datePlaceholder}
                                        onChange={this._capture('senddate', this.sendDateRef)}
                                    />
                                }
                            </div>
                        </div>
                        <p className='locationRow'>
                            <span className='label'>{resources.locationCorrectText}{this._getCurrentLocationName()}, </span>
                            <a role='button' className='changeLocation' onClick={this._changeLocation}>{resources.locationChangeText}</a>
                        </p>
                        <div className={`recipientemail ${this.state.inputLabelClasses[`recipientemail`].toString()}`}>
                            {/* <label className='label'>{resources.recipientEmailPlaceholder}</label> */}
                            <input
                                ref={this.recipientEmailRef}
                                type='text'
                                className='recipientemail-input'
                                aria-label={resources.recipientEmailPlaceholder}
                                placeholder={resources.recipientEmailPlaceholder}
                                onChange={this._capture('recipientemail', this.recipientEmailRef)}
                            />
                        </div>
                    </div>
                    <div className='giftCardStep'>
                        <div className='personaliseCard'>
                            <div className={`to ${this.state.inputLabelClasses[`recipientname`].toString()}`}>
                                <input
                                    ref={this.recipientNameRef}
                                    type='text'
                                    className='to-input'
                                    aria-label={resources.recipientPlaceholder}
                                    placeholder={resources.recipientPlaceholder}
                                    onChange={this._capture('recipientname', this.recipientNameRef)}
                                    maxLength={60}
                                />
                            </div>
                            <div className={`from ${this.state.inputLabelClasses[`fromname`].toString()}`}>
                                <input
                                    ref={this.fromNameRef}
                                    type='text'
                                    className='from-input'
                                    aria-label={resources.senderPlaceholder}
                                    placeholder={resources.senderPlaceholder}
                                    onChange={this._capture('fromname', this.fromNameRef)}
                                    maxLength={60}
                                />
                            </div>
                            <div className={`message ${this.state.inputLabelClasses[`message`].toString()}`}>
                                <textarea ref={this.messageRef} placeholder={resources.messagePlaceholder} onKeyUp={this._onEditMessage} aria-label='Message Area' className='message-textarea' onChange={this._capture('message', this.messageRef)} />
                            </div>
                            <div className='message-characters'>
                                {`${this.state.charactersLeft} ${resources.messageCharactersLimitLeftText}`}
                            </div>
                        </div>
                    </div>
                    <div className='giftCardStep'>
                        <div className='createCard'>
                            <button className='contact-form-send-button btn btn-thirdary' title={resources.createButtonText} onClick={this._onClickCreateCard} aria-label={resources.createButtonText}>
                                {resources.createButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private getMarket = (url: string = ''): string => {
        let indexOfCountry = 3;
        if (url.indexOf('test.') > 0 || url.indexOf('cittab.') > 0) {
            indexOfCountry = 4;
        }
        let market = (url && url.length > 0) 
            ? (url.indexOf('//') > -1 
                ? ((url.split('/')[indexOfCountry] === undefined || url.split('/')[indexOfCountry].length > 2 || url.split('/')[indexOfCountry] === '') 
                    ? Market.NZ 
                    : url.split('/')[indexOfCountry].toUpperCase()) 
                : Market.NZ) 
            : '';

        if (market === 'AU') {
            market = Market.AU;
        }
        return market;
    };

    private _getCurrentLocationName = ():string => {
        if (MsDyn365.isBrowser) {
            const url = this.props.context.request.url && this.props.context.request.url.requestUrl && this.props.context.request.url.requestUrl.href;
            const requestedMarket = this.getMarket(url);
            if (requestedMarket === 'AUS') {
                return 'Australia';
            }
        }
        return 'New Zealand';
    }

    private _changeLocation = () => {
        if (MsDyn365.isBrowser) {
            const url = this.props.context.request.url && this.props.context.request.url.requestUrl && this.props.context.request.url.requestUrl.href;
            const requestedMarket = this.getMarket(url);
            const baseUrl = window.location.origin;
            if (baseUrl.length) {
                if (requestedMarket === 'AUS') {
                    window.location.assign(`${baseUrl}/citta-giftcard`);
                } 
                else {
                    window.location.assign(`${baseUrl}/au/citta-giftcard`);
                } 


            }
        }
    }

    private _renderErrors = (): JSX.Element => {
        const {resources} = this.props;
        return (
            <>
                <div className={this.state.recipientEmailErrorClass}>
                    {resources.recipientEmailError}
                </div>
                <div className={this.state.sendDateErrorClass}>
                    {resources.sendDateError}
                </div>
                <div className={this.state.amountErrorClass}>
                    {resources.amountError}
                </div>
                <div className={this.state.recipientNameErrorClass}>
                    {resources.recipientNameError}
                </div>
                <div className={this.state.fromNameErrorClass}>
                    {resources.fromNameError}
                </div>
                <div className={this.state.messageErrorClass}>
                    {resources.messageError}
                </div>
                <div className={this.state.apiErrorClass}>
                    {resources.apiError}
                </div>
            </>
        );
    }

    private _getProduct = async (): Promise<SimpleProduct | null> => {
        return await getByIdAsync(
            { callerContext: this.props.context.actionContext },
            parseInt(this.props.config.productID, 10),
            this.props.context.request.apiSettings.channelId).catch(error => {
                return null;
            });
    }

    private _loadData = (): void => {
        const { config, data } = this.props;
        const cartLines = data.cart && data.cart.result && data.cart.result.cart && data.cart.result.cart.CartLines;
        const giftCartLine = cartLines && cartLines.filter(cartline => cartline.IsGiftCardLine === true);
        const giftCardOpts = giftCartLine && giftCartLine[0] && giftCartLine[0].ExtensionProperties || [];
        const { inputLabelClasses, userInput } = this.state;
        let hasRecipientEmail = false;
        let recipientName = '';
        let fromName = '';
        if (giftCardOpts.length) {
            giftCardOpts.forEach(opt => {
                const key = opt.Key;
                const value = opt.Value && opt.Value.StringValue || '';
                let amount = opt.Value && opt.Value.DecimalValue && opt.Value.DecimalValue.toString() || '';
                switch (key) {
                    case 'cardtype': {
                        this._onTypeChange(value);
                        break;
                    }
                    case 'recipientemail': {
                        this.recipientEmailRef.current!.value = value;
                        hasRecipientEmail = true;
                        break;
                    }
                    case 'senddate': {
                        const date = this._getDate(value);
                        this.sendDateRef.current!.value = date;
                        this.setState({ defaultDate: date, sendDate: date, isSendDateChangedClass: 'changed' });
                        break;
                    }
                    case 'amount': {
                        amount = `$${amount}`;
                        switch (amount) {
                            case config.opt1: {
                                this._onOptsChange('opt_1');
                                break;
                            }
                            case config.opt2: {
                                this._onOptsChange('opt_2');
                                break;
                            }
                            case config.opt3: {
                                this._onOptsChange('opt_3');
                                break;
                            }
                            default: {
                                this.amountRef.current!.value = amount.replace('$', '');
                                this._onOptsChange('opt_4');
                            }
                        }
                        break;
                    }
                    case 'cardcolour': {
                        switch (value) {
                            case config.thumb1Color: {
                                this._onThumbsChange('thumb_1');
                                break;
                            }
                            case config.thumb2Color: {
                                this._onThumbsChange('thumb_2');
                                break;
                            }
                            case config.thumb3Color: {
                                this._onThumbsChange('thumb_3');
                                break;
                            }
                            case config.thumb4Color: {
                                this._onThumbsChange('thumb_4');
                                break;
                            }
                            default: {
                                // Do nothing
                            }
                        }
                        break;
                    }
                    case 'colourpalette': {
                        switch (value) {
                            case config.thumb1Palette1Color:
                            case config.thumb2Palette1Color:
                            case config.thumb3Palette1Color: {
                                this._onColorChange('color_1');
                                break;
                            }
                            case config.thumb1Palette2Color:
                            case config.thumb2Palette2Color:
                            case config.thumb3Palette2Color: {
                                this._onColorChange('color_2');
                                break;
                            }
                            case config.thumb1Palette3Color:
                            case config.thumb2Palette3Color:
                            case config.thumb3Palette3Color: {
                                this._onColorChange('color_3');
                                break;
                            }
                            default: {
                                // Do nothing
                            }
                        }
                        break;
                    }
                    case 'recipientname': {
                        this.recipientNameRef.current!.value = value;
                        recipientName = value;
                        break;
                    }
                    case 'fromname': {
                        this.fromNameRef.current!.value = value;
                        fromName = value;
                        break;
                    }
                    case 'message': {
                        this.messageRef.current!.value = value;
                        hasRecipientEmail &&
                            this.setState({ inputLabelClasses: { ...inputLabelClasses, ['recipientemail']: 'hasvalue', ['fromname']: 'hasvalue', ['recipientname']: 'hasvalue', ['message']: 'hasvalue' } });
                        !hasRecipientEmail &&
                            this.setState({ inputLabelClasses: { ...inputLabelClasses, ['fromname']: 'hasvalue', ['recipientname']: 'hasvalue', ['message']: 'hasvalue' } });
                        this.setState({ userInput: { ...userInput, ['fromname']: fromName, ['recipientname']: recipientName }, message: value });
                        break;
                    }
                    default: {
                        // Do nothing
                    }
                }
            });
        }
    }

    private _onTypeChange = (type: string) => {
        type = type.toLocaleLowerCase();
        switch (type) {
            case 'email': {
                this.setState({ isEmailOptSelectedClass: 'selected', isPrintOptSelectedClass: '', isEmail: true });
                break;
            }
            case 'print': {
                this.setState({ isPrintOptSelectedClass: 'selected', isEmailOptSelectedClass: '', isEmail: false, sendDate: '', isSendDateChangedClass: '' });
                this.setState({ inputLabelClasses: { ...this.state.inputLabelClasses, [`recipientemail`]: '' } });
                break;
            }
            default: {
                // Do nothing
            }
        }
        this._updateUserInput('cardtype', type);
    }

    private _getDate = (date: string): string => {
        if (date) {
            const monthName: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const dateSplit = date.replace(',', '').split(' ');
            const twoDigitDay = dateSplit[0] && dateSplit[0].length === 1 ? `0${dateSplit[0]}` : `${dateSplit[0]}`;
            const month = monthName.indexOf(dateSplit[1]) + 1;
            const twoDigitMonth = month < 10 ? `0${month}` : `${month}`;
            return `${dateSplit[2]}-${twoDigitMonth}-${twoDigitDay}`;
        } else {
            return 'mm/dd/yyyy';
        }
    }

    private _onOptsChangeEvent = (option: string) => (event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => {
        this._onOptsChange(option);
    }

    private _onOptsChange = (option: string) => {
        const { config } = this.props;
        option = option.toLocaleLowerCase();
        this.setState({ isAmount1SelectedClass: '', isAmount2SelectedClass: '', isAmount3SelectedClass: '' });
        switch (option) {
            case 'opt_1': {
                this.setState({ cardAmount: config.opt1, isAmount1SelectedClass: 'selected' });
                this.amountRef.current!.value = '';
                this._updateUserInput('amount', config.opt1 || '');
                break;
            }
            case 'opt_2': {
                this.setState({ cardAmount: config.opt2, isAmount2SelectedClass: 'selected' });
                this.amountRef.current!.value = '';
                this._updateUserInput('amount', config.opt2 || '');
                break;
            }
            case 'opt_3': {
                this.setState({ cardAmount: config.opt3, isAmount3SelectedClass: 'selected' });
                this.amountRef.current!.value = '';
                this._updateUserInput('amount', config.opt3 || '');
                break;
            }
            case 'opt_4': {
                this.setState({
                    cardAmount: this.amountRef.current && this.amountRef.current.value && `$${this.amountRef.current.value}` || '',
                    inputLabelClasses: { ...this.state.inputLabelClasses, [`amount`]: this.amountRef.current && this.amountRef.current.value.length > 0 ? `hasvalue` : '' },
                });
                this._updateUserInput('amount', this.amountRef.current && this.amountRef.current.value && `$${this.amountRef.current.value}` || '');
                break;
            }
            default: {
                // Do nothing
            }
        }
        this.setState({ inputLabelClasses: { ...this.state.inputLabelClasses, [`amount`]: this.amountRef.current && this.amountRef.current.value.length > 0 ? `hasvalue` : '' } });
    }

    private _onThumbsChange = (thumb: string) => {
        const { config } = this.props;
        thumb = thumb.toLocaleLowerCase();
        this.setState({ isThumb1SelectedClass: '', isThumb2SelectedClass: '', isThumb3SelectedClass: '', isThumb4SelectedClass: '', selectedThumb: '', thumbColor: '' });
        switch (thumb) {
            case 'thumb_1': {
                this.setState({ isThumb1SelectedClass: 'selected', selectedThumb: 'thumb1', thumbColor: config.thumb1Color, selectedThumbId: 0 });
                this._updateUserInput('cardcolour', config.thumb1Color || '');
                break;
            }
            case 'thumb_2': {
                this.setState({ isThumb2SelectedClass: 'selected', selectedThumb: 'thumb2', thumbColor: config.thumb2Color, selectedThumbId: 1 });
                this._updateUserInput('cardcolour', config.thumb2Color || '');
                break;
            }
            case 'thumb_3': {
                this.setState({ isThumb3SelectedClass: 'selected', selectedThumb: 'thumb3', thumbColor: config.thumb3Color, selectedThumbId: 2 });
                this._updateUserInput('cardcolour', config.thumb3Color || '');
                break;
            }
            case 'thumb_4': {
                this.setState({ isThumb4SelectedClass: 'selected', selectedThumb: 'thumb4', thumbColor: config.thumb4Color, selectedThumbId: 3 });
                this._updateUserInput('cardcolour', config.thumb4Color || '');
                break;
            }
            default: {
                // Do nothing
            }
        }
    }

    private _onColorChange = (color: string) => {
        color = color.toLocaleLowerCase();
        this.setState({ isPaletteColor1SelectedClass: '', isPaletteColor2SelectedClass: '', isPaletteColor3SelectedClass: '', selectedPalette: '' });
        switch (color) {
            case 'color_1': {
                this.setState({ isPaletteColor1SelectedClass: 'selected', selectedPalette: 'palette1', selectedPaletteId: 0 });
                this._updateUserInput('colourpalette', this._getThumbPaletteColor(this.state.selectedThumb || 'thumb1', 'palette1'));
                break;
            }
            case 'color_2': {
                this.setState({ isPaletteColor2SelectedClass: 'selected', selectedPalette: 'palette2', selectedPaletteId: 1 });
                this._updateUserInput('colourpalette', this._getThumbPaletteColor(this.state.selectedThumb || 'thumb1', 'palette2'));
                break;
            }
            case 'color_3': {
                this.setState({ isPaletteColor3SelectedClass: 'selected', selectedPalette: 'palette3', selectedPaletteId: 2 });
                this._updateUserInput('colourpalette', this._getThumbPaletteColor(this.state.selectedThumb || 'thumb1', 'palette3'));
                break;
            }
            default: {
                // Do nothing
            }
        }
    }

    private _updateUserInput = (key: string, value: string): void => {
        const { userInput } = this.state;
        this.setState({ userInput: { ...userInput, [key]: value } });
    }

    private _getThumbPaletteColor = (thumb: string, palette: string): string => {
        const { config } = this.props;
        thumb = thumb.toLocaleLowerCase();
        palette = palette.toLocaleLowerCase();
        let color = '';
        if (thumb === 'thumb1') {
            if (palette === 'palette1') {
                color = config.thumb1Palette1Color || '';
            } else if (palette === 'palette2') {
                color = config.thumb1Palette2Color || '';
            } else if (palette === 'palette3') {
                color = config.thumb1Palette3Color || '';
            }
        } else if (thumb === 'thumb2') {
            if (palette === 'palette1') {
                color = config.thumb2Palette1Color || '';
            } else if (palette === 'palette2') {
                color = config.thumb2Palette2Color || '';
            } else if (palette === 'palette3') {
                color = config.thumb2Palette3Color || '';
            }
        } else if (thumb === 'thumb3') {
            if (palette === 'palette1') {
                color = config.thumb3Palette1Color || '';
            } else if (palette === 'palette2') {
                color = config.thumb3Palette2Color || '';
            } else if (palette === 'palette3') {
                color = config.thumb3Palette3Color || '';
            }
        } else if (thumb === 'thumb4') {
            if (palette === 'palette1') {
                color = config.thumb4Palette1Color || '';
            } else if (palette === 'palette2') {
                color = config.thumb4Palette2Color || '';
            } else if (palette === 'palette3') {
                color = config.thumb4Palette3Color || '';
            }
        }
        return color;
    }

    private _capture = (key: string, ref: React.RefObject<HTMLInputElement> | React.RefObject<HTMLTextAreaElement>) => (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { inputLabelClasses, userInput } = this.state;
        const isNotEmpty = (ref.current && ref.current.value && ref.current.value.length > 0) && 'hasvalue' || '';
        if (key === 'senddate' && isNotEmpty) {
            const date = ref.current && ref.current.value || '';
            const formattedDate = this._changeDateFormat(date);
            this.setState({ isSendDateChangedClass: 'changed', sendDate: formattedDate, defaultDate: date });
            this.setState({ userInput: { ...userInput, [key]: formattedDate } });
        } else if (key === 'senddate' && !isNotEmpty) {
            this.setState({ isSendDateChangedClass: '', sendDate: '' });
        } else {
            this.setState({ inputLabelClasses: { ...inputLabelClasses, [key]: isNotEmpty } });
            this.setState({ userInput: { ...userInput, [key]: ref.current && ref.current.value || '' } });
        }
    }

    private _changeDateFormat = (date: string): string => {
        if (date) {
            const monthName: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const dateSplit = date.split('-');
            const month = dateSplit[1] && monthName[parseInt(dateSplit[1], 10) - 1] || '';
            return `${dateSplit[2] || ''} ${month}, ${dateSplit[0] || ''}`;
        } else {
            return 'mm/dd/yyyy';
        }
    }

    private _getMinDate = (): string => {
        const today: Date = new Date();
        const dd: string = String(today.getDate()).padStart(2, '0');
        const mm: string = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy: string = today.getFullYear().toString();
        return `${yyyy}-${mm}-${dd}`;
    }

    private _onEditMessage = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        const charactersLeft: number = this.state.charactersLeft || 0;
        const key: string = event.key.length > 1 ? '' : event.key;
        const textArea: HTMLTextAreaElement | null = this.messageRef && this.messageRef.current && this.messageRef.current;
        const message = this.messageRef && this.messageRef.current && this.messageRef.current.value || '';
        let messageEnterd: string = message || key;
        const charactersLimit = this.props.config.messageCharactersLimit || 0;
        if (charactersLeft === 0 && event.which !== 8) {
            event.preventDefault();
        }
        if (charactersLeft <= 0) {
            messageEnterd = messageEnterd.substring(0, charactersLimit);
            textArea!.value = messageEnterd;
        }
        if (key.length === 1 || event.which === 8 || event.which === 46) {
            this.setState({
                message: messageEnterd,
                charactersLeft: (charactersLimit - messageEnterd.length) >= 0 ? charactersLimit - messageEnterd.length : 0
            });
        }
    }

    private _onClickCreateCard = async (): Promise<void> => {
        this._showErrors();
        this.userChoice = {
            cardtype: this.state.userInput[`cardtype`],
            recipientemail: this.state.userInput[`recipientemail`],
            senddate: this.state.userInput[`senddate`],
            amount: this.state.userInput[`amount`],
            cardcolour: this.state.userInput[`cardcolour`],
            colourpalette: this.state.userInput[`colourpalette`],
            recipientname: this.state.userInput[`recipientname`],
            fromname: this.state.userInput[`fromname`],
            message: this.state.userInput[`message`],
            hasErrors: this._hasErrors()
        };

        this._addProductToCart();
    }

    private _showErrors = (): void => {
        this.setState({
            sendDateErrorClass: (this.state.isEmail && this.state.sendDate === '') ? 'show' : 'hide',
            recipientEmailErrorClass: this._checkEmailError() ? 'show' : 'hide',
            amountErrorClass: this._checkAmountError() ? 'show' : 'hide',
            recipientNameErrorClass: (this.recipientNameRef.current && this.recipientNameRef.current.value.length === 0) ? 'show' : 'hide',
            fromNameErrorClass: (this.fromNameRef.current && this.fromNameRef.current.value.length === 0) ? 'show' : 'hide',
            messageErrorClass: (this.messageRef.current && this.messageRef.current.value.length === 0) ? 'show' : 'hide',
            apiErrorClass: (this.product === null || this.product && this.product.RecordId === 0) ? 'show' : 'hide'
        });
    }

    private _hasErrors = (): string => {
        const sendDateValue = this.state.isEmail && this.state.sendDate;
        const recipientEmailValue = this.state.isEmail && this.recipientEmailRef.current && this.recipientEmailRef.current.value;
        const recipientNameValue = this.recipientNameRef.current && this.recipientNameRef.current.value;
        const fromNameValue = this.fromNameRef.current && this.fromNameRef.current.value;
        const messageValue = this.messageRef.current && this.messageRef.current.value;
        let hasErrors: string = 'false';
        if (sendDateValue === '' || recipientEmailValue === '' || recipientNameValue === '' || this._checkEmailError() === true || this._checkAmountError() === true || fromNameValue === '' || messageValue === '') {
            hasErrors = 'true';
        }
        return hasErrors;
    }

    private _checkEmailError = (): boolean => {
        if (this.state.isEmail) {
            const email: string = this.recipientEmailRef.current && this.recipientEmailRef.current.value || '';
            if (email.length === 0) {
                return true;
            } else {
                const regex: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                const isValid: boolean = regex.test(email);
                return !isValid;
            }
        }
        return false;
    }

    private _checkAmountError = (): boolean => {
        const amount: string = this.state.cardAmount && this.state.cardAmount.replace('$', '') || '';
        if (amount.length === 0) {
            return true;
        } else {
            const regex: RegExp = /^\d+(?:\.\d{0,2})?$/;
            const isValid: boolean = regex.test(amount);
            const isAboveOrEqualToTen: boolean = isValid && (parseFloat(amount) >= 10) || false;
            return !(isValid && isAboveOrEqualToTen);
        }
    }

    private _renderAddToCartErrorBlock = (): JSX.Element | null => {
        let errorMessages: (string | undefined)[] = [];

        if (this.props.data.configErrorState.result && this.props.data.configErrorState.result.errorHost === this.props.typeName) {
            errorMessages = Object.values(this.props.data.configErrorState.result.configureErrors).filter(message => message !== undefined);
        }

        if (this.state.addToCartFailed) {
            errorMessages.push(this.state.addToCartErrorMessage);
        }

        if (this.hasGiftCardLineItem > 0) {
            errorMessages.push('One gift card has already been added to cart');
        }

        return (
            <div className={`alert alert-danger ${errorMessages.length > 0 ? '' : 'd-none'}`} role='alert' aria-live='assertive' aria-label={this.props.resources.buyboxErrorMessageHeader} >
                <div className='alert-header' aria-hidden='true'>
                    <span className='fas fa-exclamation-triangle alert-glyph' />
                    <span>{this.props.resources.maxGiftCardLimitMessage}</span>
                </div>
            </div>
        );
    }

    private _canAddToCart = (): boolean => {
        if (!this.props.data || !this.product) {
            // No product exists, won't be able to add to cart
            return false;
        }

        if (this.props.data.keyedInPriceError && this.props.data.keyedInPriceError.result) {
            return false;
        }

        if (!this.userChoice || Object.keys(this.userChoice).length === 0 || this.userChoice[`hasErrors`] === 'true') {
            // No gift card options exists, won't be able to add to cart
            return false;
        }

        // Allow only one giftcard line
        if (this.props.data.cart.result && this.props.data.channelConfiguration.result) {
            const { cart, channelConfiguration } = this.props.data;
            const cartResult = cart && cart.result;
            const channelGiftCardItemId = channelConfiguration && channelConfiguration.result && channelConfiguration.result.GiftCardItemId || '';
            const cartLines = cartResult && cartResult.cart && cartResult.cart.CartLines || [];
            for (let i = 0; i < cartLines.length; i++) {
                const itemId = cartLines[i].ItemId || '';
                if (itemId.toLocaleLowerCase() === channelGiftCardItemId.toLocaleLowerCase()) {
                    this.hasGiftCardLineItem += 1;
                    this.setState({addToCartFailed: true});
                    return false;
                }
            }
        }

        return true;
    };

    // tslint:disable: max-func-body-length tslint:disable: cyclomatic-complexity
    private _addProductToCart = async (): Promise<void> => {
        const product: SimpleProduct = this.product || { RecordId: 0, ProductTypeValue: 0, BasePrice: 0, Price: 0, AdjustedPrice: 0 };

        if (this.props.context && !this.state.disabledOverride && product.RecordId !== 0) {
            if (!this._canAddToCart()) {
                const keyedInPriceError = this.props.data.keyedInPriceError.result;
                if (keyedInPriceError) {
                    getNotificationMessage(
                        createNotificationMessage({ message: keyedInPriceError, messagetype: NotificationType.error }),
                        this.props.context.actionContext
                    );
                }

                return;
            }

            const cartState = await getCartState(this.props.context.actionContext);
            if (cartState && cartState.cart && cartState.cart.CartLines) {
                this.setState({
                    disabledOverride: true
                });

                const channelGiftCardItemId = this.props.data.channelConfiguration.result!.GiftCardItemId;
                const productItemId = product.ItemId;
                const giftCardOpts: IDictionary<string> | undefined = this.userChoice;
                const emailDeliveryModeCode: string | undefined = this.props.data.channelConfiguration.result && this.props.data.channelConfiguration.result.EmailDeliveryModeCode;
                const amount: number = Number(giftCardOpts && giftCardOpts[`amount`].replace('$', ''));
                product.Price = amount;
                product.IsGiftCard = true;

                if (channelGiftCardItemId !== productItemId) {
                    return;
                }

                const addProductsToCartInput = {
                    product: product,
                    count: 1,
                    availableQuantity: this.props.data.quantity.result,
                    enableStockCheck: this.props.context.app.config.enableStockCheck,
                    isAddEmailDeliveryItemToCart: true,
                    customPrice: amount,
                    deliveryMode: emailDeliveryModeCode
                };
                let isAddedToCart:boolean = false;
                await cartState.addProductToCart(addProductsToCartInput)
                    .then(async result => {
                        if (result.status === 'SUCCESS') {
                            isAddedToCart = true;
                            // add extension properties once CartLine
                            if (this.props.context) {
                                const message = (this.props.data.quantity.result! > 1) ?
                                    `${this.props.data.quantity.result} items added to your cart`
                                    : `${this.props.data.quantity.result} item added to your cart`;
                                getNotificationMessage(
                                    createNotificationMessage({ message, messagetype: NotificationType.success }),
                                    this.props.context.actionContext
                                );
                            }
                        } else {
                            this.setState({
                                disabledOverride: false,
                                addToCartFailed: true,
                                addToCartErrorMessage: this._getAddToCartErrorMessage(result)
                            });
                        }
                    })
                    .catch(error => {
                        this.props.telemetry.error('Error while adding to cart', error);
                    });

                if (isAddedToCart && cartState.cart && cartState.cart.CartLines) {
                        const cartLines = cartState.cart.CartLines.filter(cartLineItem => cartLineItem.ItemId === channelGiftCardItemId);
                        let updatedCart: Cart = cartState.cart;
                        let isCartLineUpdated: boolean = false;
                        if (cartLines) {
                            cartLines.map(async cLine => {
                                Object.entries(giftCardOpts).map(([key]) => {
                                    if (key.toLocaleLowerCase() !== 'haserrors') {
                                        this.addCartlineExtension(cLine, key, giftCardOpts[key]);
                                    }
                                });
                                cLine.IsGiftCardLine  = true;
                                cLine.ElectronicDeliveryEmail = cLine.ExtensionProperties && cLine.ExtensionProperties.filter(item => item.Key === "recipientemail")[0].Value?.StringValue;
                                // Updates cart with Attribute Values
                                await updateCartLinesAsync({ callerContext: this.props.context.actionContext }, cartState.cart.Id, [cLine], null)
                                .then(latestCart => {
                                    updatedCart = latestCart;
                                    isCartLineUpdated = true;

                                    if(isCartLineUpdated && updatedCart.CartLines && updatedCart.CartLines?.length > 0) {
                                        // Update cart with attribute
                                        cartState.updateCart({newCartObject: updatedCart})
                                            .catch(error => {
                                                this.props.context.actionContext.telemetry.warning(error);
                                                this.props.context.actionContext.telemetry.debug('Unable to Update Cart Line');
                                                return {cart: undefined, status: 'FAILED'};
                                            });
                                    }
                                    if (this.props.config.shouldNavigateToCart) {
                                        window.location.href = getUrlSync('cart', this.props.context.actionContext) || '';
                                    } else {
                                        this.setState({
                                            disabledOverride: false,
                                            addToCartFailed: false
                                        });
                                    }
                                },
                                reason => {
                                    this.setState({
                                        disabledOverride: false,
                                        addToCartFailed: false
                                    });
                                    this.props.context.actionContext.telemetry.error('Error while updating cart lines (Attempt 1)', reason);

                                }).catch(e => {
                                    this.setState({
                                        disabledOverride: false,
                                        addToCartFailed: false
                                    });
                                    console.log('error > ', e)
                                });
                            });
                            
                        }
                    }
            } else {
                this.setState({
                    addToCartFailed: true,
                    addToCartErrorMessage: this.props.resources.addedToCartFailureMessage
                });
            }
        }
    }

    private _getAddToCartErrorMessage = (result: ICartActionResult): string => {
        if (result.status === 'SUCCESS') {
            return '';
        } else {
            return this.props.resources.addedToCartFailureMessage;
        }
    }

    // add extension properties
    private addCartlineExtension = (cartLine: CartLine, extensionPropertyKey: string, extensionPropertyValue: string) => {
        cartLine.ExtensionProperties = cartLine.ExtensionProperties || [];
        const property = cartLine.ExtensionProperties.find((extension) => extension.Key === extensionPropertyKey);
        let amount = 0;
        if (extensionPropertyKey.toLocaleLowerCase() === 'amount') {
            amount = parseFloat(extensionPropertyValue.replace('$', ''));
        }
        if (property) {
            if (amount) {
                property.Value = { DecimalValue: amount };
            } else {
                property.Value = { StringValue: extensionPropertyValue };
            }
        } else {
            if (amount) {
                cartLine.ExtensionProperties.push({
                    Key: extensionPropertyKey,
                    Value: {
                        DecimalValue: amount
                    }
                });
            } else {
                cartLine.ExtensionProperties.push({
                    Key: extensionPropertyKey,
                    Value: {
                        StringValue: extensionPropertyValue
                    }
                });
            }
        }
    }
}

export default CittaGiftCardCreation;
